import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/App';
import { useConfig } from '@/context/ConfigContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { 
  Users, Activity, TrendingUp, Target, Clock, UserCheck, AlertTriangle, 
  CheckCircle2, XCircle, Sparkles, Brain, RefreshCw, Loader2, 
  ChevronRight, AlertCircle, Gauge, Calculator, Lightbulb, BarChart3,
  Calendar, CalendarDays, UserMinus, FileText, ShieldAlert, Plus, Edit, Trash2,
  Save, X, ChevronDown, Database
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DISCIPLINES = ['RN', 'CM', 'LPN', 'Wound RN', 'PRN', 'Chaplain', 'SW', 'Aide'];
const TEAMS = ['Blue', 'Yellow', 'Green'];

const COLORS = {
  green: '#10B981',
  yellow: '#F59E0B',
  red: '#EF4444',
  blue: '#3B82F6',
  purple: '#8B5CF6',
  indigo: '#6366F1',
  cyan: '#06B6D4',
  emerald: '#10B981',
  amber: '#F59E0B',
  rose: '#F43F5E',
  teal: '#14B8A6'
};

const CHART_COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

const TeamBadge = ({ team }) => {
  const colors = {
    'Blue': { bg: 'bg-gradient-to-r from-blue-500 to-cyan-500', text: 'text-white' },
    'Yellow': { bg: 'bg-gradient-to-r from-amber-400 to-yellow-500', text: 'text-black' },
    'Green': { bg: 'bg-gradient-to-r from-emerald-500 to-teal-500', text: 'text-white' },
    'Wound RN': { bg: 'bg-gradient-to-r from-purple-500 to-violet-500', text: 'text-white' },
  };
  const color = colors[team] || { bg: 'bg-gray-200', text: 'text-gray-800' };
  return <Badge className={`${color.bg} ${color.text} font-medium shadow-sm`}>{team || 'N/A'}</Badge>;
};

const AvailabilityBadge = ({ status, color }) => {
  const colorMap = {
    'green': 'bg-gradient-to-r from-emerald-500 to-teal-500',
    'yellow': 'bg-gradient-to-r from-amber-400 to-orange-500',
    'red': 'bg-gradient-to-r from-rose-500 to-red-500',
    'blue': 'bg-gradient-to-r from-blue-500 to-indigo-500'
  };
  return (
    <Badge className={`${colorMap[color] || 'bg-gray-500'} text-white font-medium shadow-sm`}>
      {status}
    </Badge>
  );
};

const CapacityGauge = ({ value, max = 100, label }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const color = percentage >= 90 ? '#F43F5E' : percentage >= 75 ? '#F59E0B' : '#10B981';
  
  return (
    <div className="text-center">
      <div className="relative inline-flex items-center justify-center">
        <svg className="w-28 h-28 transform -rotate-90 drop-shadow-lg">
          <circle cx="56" cy="56" r="48" stroke="#e5e7eb" strokeWidth="10" fill="none" />
          <circle
            cx="56" cy="56" r="48"
            stroke={color}
            strokeWidth="10"
            fill="none"
            strokeDasharray={`${percentage * 3.02} 302`}
            strokeLinecap="round"
            className="transition-all duration-700 ease-out"
          />
        </svg>
        <span className="absolute text-2xl font-bold bg-gradient-to-br from-slate-700 to-slate-900 bg-clip-text text-transparent">{value.toFixed(0)}%</span>
      </div>
      <p className="text-sm text-muted-foreground mt-2 font-medium">{label}</p>
    </div>
  );
};

export default function CapacityProjection() {
  const { token, user } = useAuth();
  const { getRNCMCaseloadTarget, getWorkforceTarget } = useConfig();
  
  // Get configurable targets from context
  const configTargets = useMemo(() => ({
    optimalVisits: getWorkforceTarget('optimal_visits_per_week', 18),
    expectationMin: getRNCMCaseloadTarget() - 2,
    expectationMax: getRNCMCaseloadTarget() + 3
  }), [getRNCMCaseloadTarget, getWorkforceTarget]);
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [clinicians, setClinicians] = useState([]);
  const [summary, setSummary] = useState(null);
  const [projection, setProjection] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzingAI, setAnalyzingAI] = useState(false);
  const [projecting, setProjecting] = useState(false);
  const [ptoImpact, setPtoImpact] = useState(null);
  const [loadingPto, setLoadingPto] = useState(false);
  const [coveragePlan, setCoveragePlan] = useState(null);
  const [generatingCoverage, setGeneratingCoverage] = useState(false);
  
  // Clinician management state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedClinician, setSelectedClinician] = useState(null);
  const [savingClinician, setSavingClinician] = useState(false);
  const [newClinician, setNewClinician] = useState({
    clinician_name: '',
    hire_date: '',
    team: 'Blue',
    discipline: 'RN',
    total_visits: 0,
    avg_time_minutes: 60,
    expectation_min: configTargets.expectationMin,
    expectation_max: configTargets.expectationMax,
    comment: ''
  });
  
  // Weekly tracking state
  const [selectedWeek, setSelectedWeek] = useState('');
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [weeklyData, setWeeklyData] = useState(null);
  const [loadingWeekly, setLoadingWeekly] = useState(false);
  
  // RN Capacity period selection (for data consistency with RN Capacity section)
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [rncmPeriods, setRncmPeriods] = useState([]);
  
  const [targetVisits, setTargetVisits] = useState(0);
  const [growthPercent, setGrowthPercent] = useState(0);
  
  const canEdit = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager';

  // Fetch available RN Capacity periods
  const fetchRncmPeriods = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/capacity-projection/rncm-periods`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRncmPeriods(data.periods || []);
        // Auto-select first period if available and no period selected
        if (data.periods?.length > 0 && !selectedPeriod) {
          setSelectedPeriod(data.periods[0].period_start);
        }
      }
    } catch (error) {
      console.error('Error fetching RN Capacity periods:', error);
    }
  }, [token, selectedPeriod]);

  const fetchData = useCallback(async () => {
    try {
      // Build query params for period filtering
      const periodParam = selectedPeriod ? `?period_start=${selectedPeriod}` : '';
      
      const [cliniciansRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/capacity-projection/clinicians${periodParam}`, { headers: { Authorization: `Bearer ${token}` }}),
        fetch(`${API_URL}/api/capacity-projection/summary${periodParam}`, { headers: { Authorization: `Bearer ${token}` }})
      ]);

      if (cliniciansRes.ok) setClinicians(await cliniciansRes.json());
      if (summaryRes.ok) setSummary(await summaryRes.json());
    } catch (error) {
      toast.error('Failed to load capacity projection data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, selectedPeriod]);

  // Fetch periods on mount
  useEffect(() => {
    fetchRncmPeriods();
  }, [fetchRncmPeriods]);

  // Fetch data when period changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchRncmPeriods();
    fetchData();
    toast.success('Data refreshed');
  };

  const handleProject = async () => {
    setProjecting(true);
    try {
      const response = await fetch(`${API_URL}/api/capacity-projection/project`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          target_visits: parseInt(targetVisits) || 0,
          growth_percent: parseFloat(growthPercent) || 0,
          timeframe_weeks: 4
        })
      });

      if (response.ok) {
        const result = await response.json();
        setProjection(result);
        toast.success('Capacity projection generated');
      } else {
        const error = await response.json();
        toast.error('Projection failed', { description: error.detail });
      }
    } catch (error) {
      toast.error('Projection error');
    } finally {
      setProjecting(false);
    }
  };

  const handleAIAnalysis = async () => {
    setAnalyzingAI(true);
    try {
      const response = await fetch(`${API_URL}/api/capacity-projection/ai-recommendation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          target_visits: parseInt(targetVisits) || 0,
          growth_percent: parseFloat(growthPercent) || 0
        })
      });

      if (response.ok) {
        const result = await response.json();
        setAiAnalysis(result);
        toast.success('AI analysis complete');
      } else {
        toast.error('AI analysis failed');
      }
    } catch (error) {
      toast.error('AI analysis error');
    } finally {
      setAnalyzingAI(false);
    }
  };

  const handleFetchPtoImpact = async () => {
    setLoadingPto(true);
    try {
      const response = await fetch(`${API_URL}/api/capacity-projection/pto-impact`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        setPtoImpact(result);
        toast.success('PTO impact analysis loaded');
      } else {
        toast.error('Failed to load PTO impact');
      }
    } catch (error) {
      toast.error('PTO impact error');
    } finally {
      setLoadingPto(false);
    }
  };

  const handleGenerateCoveragePlan = async () => {
    setGeneratingCoverage(true);
    try {
      const response = await fetch(`${API_URL}/api/capacity-projection/pto-coverage-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({})
      });

      if (response.ok) {
        const result = await response.json();
        setCoveragePlan(result);
        toast.success('Coverage plan generated');
      } else {
        toast.error('Failed to generate coverage plan');
      }
    } catch (error) {
      toast.error('Coverage plan error');
    } finally {
      setGeneratingCoverage(false);
    }
  };

  // Clinician CRUD handlers
  const handleAddClinician = async () => {
    setSavingClinician(true);
    try {
      const response = await fetch(`${API_URL}/api/capacity-projection/clinicians/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(newClinician)
      });

      if (response.ok) {
        toast.success('Clinician added successfully');
        setShowAddModal(false);
        setNewClinician({
          clinician_name: '', hire_date: '', team: 'Blue', discipline: 'RN',
          total_visits: 0, avg_time_minutes: 60, expectation_min: 15, expectation_max: 20, comment: ''
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Failed to add clinician', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error adding clinician');
    } finally {
      setSavingClinician(false);
    }
  };

  const handleUpdateClinician = async () => {
    if (!selectedClinician) return;
    setSavingClinician(true);
    try {
      const response = await fetch(`${API_URL}/api/capacity-projection/clinicians/${selectedClinician.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(selectedClinician)
      });

      if (response.ok) {
        toast.success('Clinician updated');
        setShowEditModal(false);
        setSelectedClinician(null);
        fetchData();
      } else {
        toast.error('Failed to update clinician');
      }
    } catch (error) {
      toast.error('Error updating clinician');
    } finally {
      setSavingClinician(false);
    }
  };

  const handleDeleteClinician = async () => {
    if (!selectedClinician) return;
    try {
      const response = await fetch(`${API_URL}/api/capacity-projection/clinicians/${selectedClinician.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Clinician deleted');
        setShowDeleteConfirm(false);
        setSelectedClinician(null);
        fetchData();
      } else {
        toast.error('Failed to delete clinician');
      }
    } catch (error) {
      toast.error('Error deleting clinician');
    }
  };

  // Weekly data handlers
  const fetchWeeks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/capacity-projection/weeks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableWeeks(data.weeks || []);
        if (!selectedWeek && data.current_week) {
          setSelectedWeek(data.current_week);
        }
      }
    } catch (error) {
      console.error('Error fetching weeks:', error);
    }
  };

  const fetchWeeklyData = async (week) => {
    if (!week) return;
    setLoadingWeekly(true);
    try {
      const response = await fetch(`${API_URL}/api/capacity-projection/weekly/${week}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWeeklyData(data);
      }
    } catch (error) {
      toast.error('Error fetching weekly data');
    } finally {
      setLoadingWeekly(false);
    }
  };

  const handleSaveWeeklyVisits = async (clinicianId, visits, avgTime) => {
    try {
      const response = await fetch(`${API_URL}/api/capacity-projection/weekly/${selectedWeek}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          clinician_id: clinicianId,
          week: selectedWeek,
          total_visits: parseInt(visits),
          avg_time_minutes: parseInt(avgTime) || 60,
          comment: ''
        })
      });

      if (response.ok) {
        toast.success('Weekly data saved');
        fetchWeeklyData(selectedWeek);
        fetchData(); // Refresh main data too
      }
    } catch (error) {
      toast.error('Error saving weekly data');
    }
  };

  useEffect(() => {
    fetchWeeks();
  }, [token]);

  useEffect(() => {
    if (selectedWeek) {
      fetchWeeklyData(selectedWeek);
    }
  }, [selectedWeek]);

  const handleResetToDefaults = async () => {
    try {
      const response = await fetch(`${API_URL}/api/capacity-projection/reset-to-defaults`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Data reset to defaults');
        fetchData();
      } else {
        toast.error('Reset failed');
      }
    } catch (error) {
      toast.error('Reset error');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="capacity-projection-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Prepare chart data
  const teamChartData = summary?.teams ? Object.entries(summary.teams)
    .filter(([name]) => !['Wound RN', 'Unknown'].includes(name))
    .map(([name, data]) => ({
      name,
      visits: data.total_visits,
      capacity: data.max_capacity,
      utilization: data.utilization,
      remaining: data.remaining
    })) : [];

  const utilizationPieData = [
    { name: 'Used', value: summary?.total_current_visits || 0, fill: COLORS.blue },
    { name: 'Available', value: summary?.total_remaining_capacity || 0, fill: COLORS.green }
  ];

  return (
    <div className="space-y-6" data-testid="capacity-projection-page">
      {/* Header with Gradient Background */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#00b0ab] via-[#009590] to-[#48494b] p-6 text-white shadow-xl">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCAyLTRzMiAyIDIgNC0yIDQtMiA0LTItMi0yLTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <Calculator className="h-7 w-7" />
              </div>
              RN/CM Capacity Projection
            </h1>
            <p className="text-white/80 mt-2 text-lg">
              Project capacity based on RN/CM availability and get recommendations
            </p>
          </div>
          <div className="flex gap-2">
            {canEdit && (
              <Button variant="secondary" onClick={() => setShowAddModal(true)} className="bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Clinician
              </Button>
            )}
            <Button variant="secondary" onClick={handleRefresh} disabled={refreshing} className="bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-sm">
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {canEdit && (
              <Button variant="secondary" onClick={handleResetToDefaults} className="bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-sm">
                Reset to Defaults
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Period Selector - Shows RN Capacity periods */}
      {rncmPeriods.length > 0 && (
        <Card className="bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-slate-600" />
                <span className="font-medium text-slate-700">Viewing RN Capacity Data for:</span>
              </div>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="w-[240px] bg-white" data-testid="period-selector">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  {rncmPeriods.map((period) => (
                    <SelectItem key={period.period_start} value={period.period_start}>
                      {period.period_start} → {period.period_end} ({period.clinician_count} clinicians)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {summary?.data_source && (
                <Badge variant={summary.data_source === 'rncm_capacity' ? 'default' : 'secondary'} className="gap-1">
                  <Database className="h-3 w-3" />
                  Source: {summary.data_source === 'rncm_capacity' ? 'RN Capacity' : 'Projection Data'}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards - Modern Bento Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card className="group relative overflow-hidden bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border-0 shadow-lg shadow-indigo-500/20 hover:shadow-xl hover:shadow-indigo-500/30 transition-all duration-300 hover:-translate-y-1">
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-indigo-100 font-medium">Active RN/CMs</p>
                <p className="text-4xl font-bold mt-1">{summary?.total_clinicians || 0}</p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <Users className="h-8 w-8" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="group relative overflow-hidden bg-gradient-to-br from-cyan-500 to-blue-600 text-white border-0 shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-300 hover:-translate-y-1">
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-cyan-100 font-medium">Current Visits</p>
                <p className="text-4xl font-bold mt-1">{summary?.total_current_visits || 0}</p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <Activity className="h-8 w-8" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="group relative overflow-hidden bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0 shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:shadow-emerald-500/30 transition-all duration-300 hover:-translate-y-1">
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-100 font-medium">Max Capacity</p>
                <p className="text-4xl font-bold mt-1">{summary?.total_max_capacity || 0}</p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <Target className="h-8 w-8" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="group relative overflow-hidden bg-gradient-to-br from-amber-400 to-orange-500 text-white border-0 shadow-lg shadow-amber-500/20 hover:shadow-xl hover:shadow-amber-500/30 transition-all duration-300 hover:-translate-y-1">
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-100 font-medium">Available</p>
                <p className="text-4xl font-bold mt-1">+{summary?.total_remaining_capacity || 0}</p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <CheckCircle2 className="h-8 w-8" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`group relative overflow-hidden border-0 shadow-lg transition-all duration-300 hover:-translate-y-1 ${
          (summary?.overall_utilization || 0) >= 90 
            ? 'bg-gradient-to-br from-rose-500 to-red-600 shadow-rose-500/20 hover:shadow-rose-500/30' 
            : (summary?.overall_utilization || 0) >= 75 
              ? 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-amber-500/20 hover:shadow-amber-500/30'
              : 'bg-gradient-to-br from-emerald-500 to-green-600 shadow-emerald-500/20 hover:shadow-emerald-500/30'
        } text-white`}>
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90 font-medium">Utilization</p>
                <p className="text-4xl font-bold mt-1">{(summary?.overall_utilization || 0).toFixed(1)}%</p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <Gauge className="h-8 w-8" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="flex flex-wrap h-auto gap-1 p-1.5 bg-slate-100 dark:bg-slate-800 rounded-xl">
          <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-4 py-2.5 transition-all">
            <BarChart3 className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="clinicians" data-testid="tab-clinicians" className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-4 py-2.5 transition-all">
            <Users className="h-4 w-4 mr-2" />
            Clinicians
          </TabsTrigger>
          <TabsTrigger value="weekly" data-testid="tab-weekly" onClick={() => !weeklyData && selectedWeek && fetchWeeklyData(selectedWeek)} className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-4 py-2.5 transition-all">
            <CalendarDays className="h-4 w-4 mr-2" />
            Weekly Data
          </TabsTrigger>
          <TabsTrigger value="projection" data-testid="tab-projection" className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-4 py-2.5 transition-all">
            <Calculator className="h-4 w-4 mr-2" />
            Project Capacity
          </TabsTrigger>
          <TabsTrigger value="pto-impact" data-testid="tab-pto" onClick={() => !ptoImpact && handleFetchPtoImpact()} className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-4 py-2.5 transition-all">
            <Calendar className="h-4 w-4 mr-2" />
            PTO Impact
          </TabsTrigger>
          <TabsTrigger value="ai-recommendations" data-testid="tab-ai" className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-4 py-2.5 transition-all">
            <Sparkles className="h-4 w-4 mr-2" />
            AI Insights
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Team Capacity Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-[#00b0ab]" />
                  Team Capacity Distribution
                </CardTitle>
                <CardDescription>Current visits vs maximum capacity by team</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={teamChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="visits" name="Current Visits" fill={COLORS.blue} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="capacity" name="Max Capacity" fill={COLORS.purple} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Utilization Pie */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gauge className="h-5 w-5 text-emerald-500" />
                  Overall Capacity Utilization
                </CardTitle>
                <CardDescription>Used capacity vs available capacity</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-around">
                  <div className="h-64 w-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={utilizationPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {utilizationPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-4">
                    <CapacityGauge 
                      value={summary?.overall_utilization || 0} 
                      label="Utilization Rate" 
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Team Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Team Breakdown</CardTitle>
              <CardDescription>Detailed capacity metrics by team</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {summary?.teams && Object.entries(summary.teams)
                  .filter(([name]) => !['Wound RN', 'Unknown'].includes(name))
                  .map(([name, data]) => (
                    <Card key={name} className="border-l-4" style={{ borderLeftColor: name === 'Blue' ? COLORS.blue : name === 'Yellow' ? COLORS.yellow : COLORS.green }}>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold flex items-center gap-2">
                            <TeamBadge team={name} />
                          </h4>
                          <span className="text-sm text-muted-foreground">{data.count} staff</span>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Current Visits:</span>
                            <span className="font-medium">{data.total_visits}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Max Capacity:</span>
                            <span className="font-medium">{data.max_capacity}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Available:</span>
                            <span className="font-medium text-emerald-600">{data.remaining}</span>
                          </div>
                          <div className="pt-2">
                            <div className="flex justify-between text-sm mb-1">
                              <span>Utilization</span>
                              <span>{data.utilization}%</span>
                            </div>
                            <Progress 
                              value={data.utilization} 
                              className={data.utilization >= 90 ? 'bg-red-100' : data.utilization >= 75 ? 'bg-amber-100' : 'bg-emerald-100'}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            </CardContent>
          </Card>

          {/* Recommendations Alert */}
          {summary?.recommendations?.length > 0 && (
            <Alert className={summary.overall_utilization >= 90 ? 'border-red-500 bg-red-50 dark:bg-red-950' : summary.overall_utilization >= 75 ? 'border-amber-500 bg-amber-50 dark:bg-amber-950' : 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950'}>
              <Lightbulb className="h-4 w-4" />
              <AlertTitle>Quick Recommendations</AlertTitle>
              <AlertDescription>
                <ul className="list-disc ml-4 mt-2 space-y-1">
                  {summary.recommendations.map((rec, idx) => (
                    <li key={idx}>{rec}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>

        {/* Clinicians Tab */}
        <TabsContent value="clinicians" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                RN/CM Availability Details
              </CardTitle>
              <CardDescription>Individual clinician capacity and availability status</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Clinician</TableHead>
                    <TableHead>Hire Date</TableHead>
                    <TableHead>Team</TableHead>
                    <TableHead>Discipline</TableHead>
                    <TableHead>Visits</TableHead>
                    <TableHead>Avg Time</TableHead>
                    <TableHead>Expected</TableHead>
                    <TableHead>Utilization</TableHead>
                    <TableHead>Status</TableHead>
                    {canEdit && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clinicians.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canEdit ? 10 : 9} className="text-center py-8 text-muted-foreground">
                        No clinician data available. Click "Reset to Defaults" to load sample data.
                      </TableCell>
                    </TableRow>
                  ) : (
                    clinicians.map((c, idx) => (
                      <TableRow key={c.id || idx} className={c.availability?.utilization_percent >= 95 ? 'bg-red-50 dark:bg-red-950/20' : ''}>
                        <TableCell className="font-medium">{c.clinician_name}</TableCell>
                        <TableCell>{c.hire_date || '—'}</TableCell>
                        <TableCell><TeamBadge team={c.team} /></TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-slate-100">{c.discipline || 'RN'}</Badge>
                        </TableCell>
                        <TableCell className="font-semibold">{c.total_visits}</TableCell>
                        <TableCell>
                          {c.avg_time_minutes ? (
                            <span className={c.avg_time_minutes > 75 ? 'text-amber-600' : c.avg_time_minutes < 45 ? 'text-blue-600' : 'text-emerald-600'}>
                              {c.avg_time_minutes} min
                            </span>
                          ) : '—'}
                        </TableCell>
                        <TableCell>{c.expectation_min}-{c.expectation_max}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress 
                              value={c.availability?.utilization_percent || 0} 
                              className="w-16 h-2"
                            />
                            <span className="text-sm">{c.availability?.utilization_percent?.toFixed(0) || 0}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <AvailabilityBadge 
                            status={c.availability?.availability_status} 
                            color={c.availability?.availability_color} 
                          />
                        </TableCell>
                        {canEdit && (
                          <TableCell>
                            <div className="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => { setSelectedClinician(c); setShowEditModal(true); }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => { setSelectedClinician(c); setShowDeleteConfirm(true); }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              
              {/* Supplementary Visits Info */}
              <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2">Supplementary Support</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">LPN Total Visits</p>
                    <p className="text-xl font-bold">{summary?.supplementary_visits?.['LPN Total'] || 29}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">PRN Total Visits</p>
                    <p className="text-xl font-bold">{summary?.supplementary_visits?.['PRN Total'] || 7}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">RNCM Total Visits</p>
                    <p className="text-xl font-bold">{summary?.total_current_visits || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Grand Total</p>
                    <p className="text-xl font-bold text-[#00b0ab]">{summary?.total_all_visits || 0}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Weekly Data Tab */}
        <TabsContent value="weekly" className="space-y-4">
          <Card>
            <CardHeader className="bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-[#00b0ab]/20 dark:to-violet-900/20">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarDays className="h-5 w-5 text-[#00b0ab]" />
                    Weekly Visit Tracking
                  </CardTitle>
                  <CardDescription>
                    Track and manage clinician visits week by week. Select a week to view and edit data.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={selectedWeek} onValueChange={setSelectedWeek}>
                    <SelectTrigger className="w-[180px] bg-white" data-testid="week-selector">
                      <SelectValue placeholder="Select week" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableWeeks.map(week => (
                        <SelectItem key={week} value={week}>{week}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {loadingWeekly ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-[#00b0ab]" />
                </div>
              ) : !selectedWeek ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CalendarDays className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">Select a Week</p>
                  <p className="text-sm mt-2">Choose a week from the dropdown above to view and edit clinician visit data</p>
                </div>
              ) : weeklyData?.clinicians?.length > 0 ? (
                <div className="space-y-4">
                  {/* Weekly Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <Card className="bg-gradient-to-br from-[#00b0ab] to-[#008a86] text-white">
                      <CardContent className="p-4">
                        <p className="text-sm opacity-80">Week</p>
                        <p className="text-2xl font-bold">{selectedWeek}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
                      <CardContent className="p-4">
                        <p className="text-sm opacity-80">Total Visits</p>
                        <p className="text-2xl font-bold">
                          {weeklyData.clinicians.reduce((sum, c) => sum + (c.weekly_visits || 0), 0)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white">
                      <CardContent className="p-4">
                        <p className="text-sm opacity-80">Avg Visits/Clinician</p>
                        <p className="text-2xl font-bold">
                          {(weeklyData.clinicians.reduce((sum, c) => sum + (c.weekly_visits || 0), 0) / weeklyData.clinicians.length).toFixed(1)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white">
                      <CardContent className="p-4">
                        <p className="text-sm opacity-80">Staff Count</p>
                        <p className="text-2xl font-bold">{weeklyData.clinicians.length}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Weekly Data Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50 dark:bg-slate-800">
                          <TableHead>Clinician</TableHead>
                          <TableHead>Team</TableHead>
                          <TableHead>Discipline</TableHead>
                          <TableHead className="text-center">Weekly Visits</TableHead>
                          <TableHead className="text-center">Avg Time (min)</TableHead>
                          <TableHead className="text-center">Expectation</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                          {canEdit && <TableHead className="text-center">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {weeklyData.clinicians.map((clinician, idx) => {
                          const visits = clinician.weekly_visits || 0;
                          const expMax = clinician.expectation_max || 20;
                          const utilization = expMax > 0 ? (visits / expMax) * 100 : 0;
                          
                          return (
                            <TableRow key={clinician.id || idx} data-testid={`weekly-row-${idx}`}>
                              <TableCell className="font-medium">{clinician.clinician_name}</TableCell>
                              <TableCell><TeamBadge team={clinician.team} /></TableCell>
                              <TableCell>
                                <Badge variant="outline">{clinician.discipline}</Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                {canEdit ? (
                                  <Input
                                    type="number"
                                    className="w-20 text-center mx-auto"
                                    defaultValue={visits}
                                    data-testid={`weekly-visits-${idx}`}
                                    onBlur={(e) => {
                                      const newVisits = parseInt(e.target.value);
                                      if (newVisits !== visits) {
                                        handleSaveWeeklyVisits(clinician.id, newVisits, clinician.weekly_avg_time);
                                      }
                                    }}
                                  />
                                ) : (
                                  <span className="font-bold text-lg">{visits}</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                {canEdit ? (
                                  <Input
                                    type="number"
                                    className="w-20 text-center mx-auto"
                                    defaultValue={clinician.weekly_avg_time || 60}
                                    data-testid={`weekly-time-${idx}`}
                                    onBlur={(e) => {
                                      const newTime = parseInt(e.target.value);
                                      if (newTime !== clinician.weekly_avg_time) {
                                        handleSaveWeeklyVisits(clinician.id, visits, newTime);
                                      }
                                    }}
                                  />
                                ) : (
                                  <span>{clinician.weekly_avg_time || 60}</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                <span className="text-sm text-muted-foreground">
                                  {clinician.expectation_min || 15}-{expMax}
                                </span>
                              </TableCell>
                              <TableCell className="text-center">
                                <AvailabilityBadge 
                                  status={
                                    utilization >= 100 ? 'At Capacity' :
                                    utilization >= 85 ? 'Near Capacity' :
                                    utilization >= 60 ? 'Available' : 'Under'
                                  }
                                  color={
                                    utilization >= 100 ? 'red' :
                                    utilization >= 85 ? 'yellow' :
                                    utilization >= 60 ? 'green' : 'blue'
                                  }
                                />
                              </TableCell>
                              {canEdit && (
                                <TableCell className="text-center">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      setSelectedClinician(clinician);
                                      setShowEditModal(true);
                                    }}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Week Navigation */}
                  <div className="flex items-center justify-between pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        const currentIdx = availableWeeks.indexOf(selectedWeek);
                        if (currentIdx < availableWeeks.length - 1) {
                          setSelectedWeek(availableWeeks[currentIdx + 1]);
                        }
                      }}
                      disabled={availableWeeks.indexOf(selectedWeek) >= availableWeeks.length - 1}
                    >
                      ← Previous Week
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Week {availableWeeks.indexOf(selectedWeek) + 1} of {availableWeeks.length}
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => {
                        const currentIdx = availableWeeks.indexOf(selectedWeek);
                        if (currentIdx > 0) {
                          setSelectedWeek(availableWeeks[currentIdx - 1]);
                        }
                      }}
                      disabled={availableWeeks.indexOf(selectedWeek) <= 0}
                    >
                      Next Week →
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No Data for This Week</p>
                  <p className="text-sm mt-2">Add clinician visit data using the Clinicians tab</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Projection Tab */}
        <TabsContent value="projection" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5 text-[#00b0ab]" />
                Capacity Projection Calculator
              </CardTitle>
              <CardDescription>
                Project if your target capacity can be achieved based on current RN/CM availability
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <Label htmlFor="target-visits">Target Visits</Label>
                  <Input
                    id="target-visits"
                    type="number"
                    value={targetVisits}
                    onChange={(e) => setTargetVisits(e.target.value)}
                    placeholder="e.g., 200"
                    data-testid="input-target-visits"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Leave 0 for current state analysis
                  </p>
                </div>
                <div>
                  <Label htmlFor="growth-percent">Growth % (4 weeks)</Label>
                  <Input
                    id="growth-percent"
                    type="number"
                    value={growthPercent}
                    onChange={(e) => setGrowthPercent(e.target.value)}
                    placeholder="e.g., 10"
                    data-testid="input-growth-percent"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Expected census growth percentage
                  </p>
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={handleProject} 
                    disabled={projecting}
                    className="w-full"
                    data-testid="btn-project"
                  >
                    {projecting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Projecting...
                      </>
                    ) : (
                      <>
                        <Calculator className="h-4 w-4 mr-2" />
                        Project Capacity
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Projection Results */}
              {projection && (
                <div className="space-y-6">
                  {/* Achievement Status */}
                  <Alert className={projection.can_achieve_target ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950' : 'border-red-500 bg-red-50 dark:bg-red-950'}>
                    {projection.can_achieve_target ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-red-600" />
                    )}
                    <AlertTitle>
                      {projection.can_achieve_target 
                        ? 'Target Achievable' 
                        : `Target NOT Achievable - Shortfall: ${projection.shortfall} visits`}
                    </AlertTitle>
                    <AlertDescription>
                      {projection.can_achieve_target 
                        ? `Your team has sufficient capacity to meet the projected demand.`
                        : `Consider hiring ${Math.ceil(projection.shortfall / 18)} additional RN/CM(s) to meet demand.`}
                    </AlertDescription>
                  </Alert>

                  {/* Projection Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">Current Visits</p>
                        <p className="text-2xl font-bold">{projection.total_current_visits}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">Max Capacity</p>
                        <p className="text-2xl font-bold">{projection.total_max_capacity}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">Available</p>
                        <p className="text-2xl font-bold text-emerald-600">+{projection.total_remaining_capacity}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">Utilization</p>
                        <p className="text-2xl font-bold">{projection.overall_utilization}%</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Growth Scenario */}
                  {projection.growth_scenario && (
                    <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
                      <CardHeader>
                        <CardTitle className="text-amber-700 dark:text-amber-400 flex items-center gap-2">
                          <TrendingUp className="h-5 w-5" />
                          Growth Scenario Analysis ({projection.growth_scenario.growth_percent}% in {projection.growth_scenario.timeframe_weeks} weeks)
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-sm text-muted-foreground">Current Visits</p>
                            <p className="text-xl font-bold">{projection.growth_scenario.current_visits}</p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Projected Visits</p>
                            <p className="text-xl font-bold">{projection.growth_scenario.projected_visits}</p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Can Achieve?</p>
                            <p className={`text-xl font-bold ${projection.growth_scenario.can_achieve ? 'text-emerald-600' : 'text-red-600'}`}>
                              {projection.growth_scenario.can_achieve ? 'YES' : 'NO'}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Shortfall</p>
                            <p className="text-xl font-bold text-red-600">
                              {projection.growth_scenario.shortfall > 0 ? projection.growth_scenario.shortfall : '—'}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Detailed Recommendations */}
                  {projection.detailed_recommendations?.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Lightbulb className="h-5 w-5 text-amber-500" />
                          Detailed Recommendations
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {projection.detailed_recommendations.map((rec, idx) => (
                            <div 
                              key={idx} 
                              className={`p-4 rounded-lg border-l-4 ${
                                rec.priority === 'HIGH' 
                                  ? 'border-red-500 bg-red-50 dark:bg-red-950/20' 
                                  : rec.priority === 'MEDIUM'
                                    ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/20'
                                    : 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge variant={rec.priority === 'HIGH' ? 'destructive' : rec.priority === 'MEDIUM' ? 'default' : 'secondary'}>
                                      {rec.priority}
                                    </Badge>
                                    <Badge variant="outline">{rec.category}</Badge>
                                  </div>
                                  <p className="font-medium">{rec.recommendation}</p>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    <span className="font-medium">Action:</span> {rec.action}
                                  </p>
                                  <p className="text-sm text-muted-foreground">
                                    <span className="font-medium">Impact:</span> {rec.impact}
                                  </p>
                                </div>
                                <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PTO Impact Tab */}
        <TabsContent value="pto-impact" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarDays className="h-5 w-5 text-amber-500" />
                    PTO Impact Analysis
                  </CardTitle>
                  <CardDescription>
                    Analyze how scheduled PTO affects team capacity and get coverage recommendations
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleFetchPtoImpact} disabled={loadingPto}>
                    {loadingPto ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh
                      </>
                    )}
                  </Button>
                  <Button onClick={handleGenerateCoveragePlan} disabled={generatingCoverage || !ptoImpact || ptoImpact?.summary?.clinicians_with_pto === 0}>
                    {generatingCoverage ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4 mr-2" />
                        Generate Coverage Plan
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingPto ? (
                <div className="space-y-4">
                  <Skeleton className="h-24" />
                  <Skeleton className="h-48" />
                </div>
              ) : !ptoImpact ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CalendarDays className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">No PTO Analysis Yet</p>
                  <p className="text-sm mt-2">Click "Refresh" to analyze PTO impact on team capacity</p>
                </div>
              ) : ptoImpact.summary?.clinicians_with_pto === 0 ? (
                <Alert className="border-emerald-500 bg-emerald-50 dark:bg-emerald-950">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <AlertTitle>No PTO Scheduled</AlertTitle>
                  <AlertDescription>
                    No clinicians have PTO notes in their records. Full capacity is available.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-6">
                  {/* PTO Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Staff on PTO</p>
                            <p className="text-2xl font-bold">{ptoImpact.summary?.clinicians_with_pto || 0}</p>
                          </div>
                          <UserMinus className="h-8 w-8 text-amber-500 opacity-80" />
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Total PTO Days</p>
                            <p className="text-2xl font-bold">{ptoImpact.summary?.total_pto_days || 0}</p>
                          </div>
                          <Calendar className="h-8 w-8 text-blue-500 opacity-80" />
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-red-500/10 to-rose-500/10">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Visits at Risk</p>
                            <p className="text-2xl font-bold text-red-600">{ptoImpact.summary?.total_visits_at_risk || 0}</p>
                          </div>
                          <AlertTriangle className="h-8 w-8 text-red-500 opacity-80" />
                        </div>
                      </CardContent>
                    </Card>
                    <Card className={`bg-gradient-to-br ${ptoImpact.capacity_context?.impact_severity === 'HIGH' ? 'from-red-500/10 to-rose-500/10' : ptoImpact.capacity_context?.impact_severity === 'MEDIUM' ? 'from-amber-500/10 to-orange-500/10' : 'from-emerald-500/10 to-green-500/10'}`}>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Impact Severity</p>
                            <p className={`text-2xl font-bold ${ptoImpact.capacity_context?.impact_severity === 'HIGH' ? 'text-red-600' : ptoImpact.capacity_context?.impact_severity === 'MEDIUM' ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {ptoImpact.capacity_context?.impact_severity || 'LOW'}
                            </p>
                          </div>
                          <ShieldAlert className={`h-8 w-8 opacity-80 ${ptoImpact.capacity_context?.impact_severity === 'HIGH' ? 'text-red-500' : ptoImpact.capacity_context?.impact_severity === 'MEDIUM' ? 'text-amber-500' : 'text-emerald-500'}`} />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Staff on PTO */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Staff with Scheduled PTO</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Clinician</TableHead>
                            <TableHead>Team</TableHead>
                            <TableHead>PTO Dates</TableHead>
                            <TableHead>Daily Capacity Loss</TableHead>
                            <TableHead>Notes</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {ptoImpact.pto_clinicians?.map((c, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{c.clinician_name}</TableCell>
                              <TableCell><TeamBadge team={c.team} /></TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {c.pto_dates?.map((d, i) => (
                                    <Badge key={i} variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
                                      {d.date_str}
                                    </Badge>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell className="text-red-600 font-medium">-{c.daily_capacity_loss} visits</TableCell>
                              <TableCell className="text-sm text-muted-foreground">{c.comment}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>

                  {/* PTO by Date */}
                  {ptoImpact.pto_by_date?.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Capacity Impact by Date</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {ptoImpact.pto_by_date?.map((d, idx) => (
                            <div 
                              key={idx} 
                              className={`p-4 rounded-lg border ${d.clinicians?.length > 1 ? 'border-red-300 bg-red-50 dark:bg-red-950/20' : 'border-amber-300 bg-amber-50 dark:bg-amber-950/20'}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="text-center">
                                    <p className="text-xl font-bold">{new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { day: 'numeric' })}</p>
                                    <p className="text-xs text-muted-foreground">{new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short' })}</p>
                                  </div>
                                  <div>
                                    <p className="font-medium">{d.clinicians?.join(', ')}</p>
                                    <p className="text-sm text-muted-foreground">
                                      Teams: {d.teams_affected?.join(', ')}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-lg font-bold text-red-600">-{d.total_capacity_loss} visits</p>
                                  {d.clinicians?.length > 1 && (
                                    <Badge variant="destructive" className="text-xs">Multiple Staff Out</Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Coverage Recommendations */}
                  {ptoImpact.coverage_recommendations?.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Lightbulb className="h-5 w-5 text-amber-500" />
                          Coverage Recommendations
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {ptoImpact.coverage_recommendations?.map((rec, idx) => (
                            <div 
                              key={idx} 
                              className={`p-4 rounded-lg border-l-4 ${
                                rec.priority === 'HIGH' 
                                  ? 'border-red-500 bg-red-50 dark:bg-red-950/20' 
                                  : rec.priority === 'MEDIUM'
                                    ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/20'
                                    : 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge variant={rec.priority === 'HIGH' ? 'destructive' : rec.priority === 'MEDIUM' ? 'default' : 'secondary'}>
                                      {rec.priority}
                                    </Badge>
                                    <Badge variant="outline">{rec.type}</Badge>
                                  </div>
                                  <p className="font-medium">{rec.issue}</p>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    <span className="font-medium">Action:</span> {rec.action}
                                  </p>
                                  {rec.visits_at_risk > 0 && (
                                    <p className="text-sm text-red-600 mt-1">
                                      <span className="font-medium">Visits at risk:</span> {rec.visits_at_risk}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* AI Coverage Plan */}
                  {coveragePlan && (
                    <Card className="border-[#00b0ab]/30 bg-[#00b0ab]/10/50 dark:bg-[#00b0ab]/10/20">
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Brain className="h-5 w-5 text-[#00b0ab]" />
                          AI-Generated Coverage Plan
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-[400px] rounded-lg border p-4 bg-white dark:bg-gray-900">
                          <div className="prose prose-sm max-w-none dark:prose-invert">
                            <ReactMarkdown
                              components={{
                                h1: ({children}) => <h1 className="text-xl font-bold mt-4 mb-2 text-[#00b0ab] dark:text-[#33c3bf]">{children}</h1>,
                                h2: ({children}) => <h2 className="text-lg font-bold mt-4 mb-2 text-[#00b0ab] dark:text-[#33c3bf]">{children}</h2>,
                                h3: ({children}) => <h3 className="text-base font-semibold mt-3 mb-1">{children}</h3>,
                                p: ({children}) => <p className="my-2 leading-relaxed">{children}</p>,
                                ul: ({children}) => <ul className="list-disc ml-4 my-2 space-y-1">{children}</ul>,
                                ol: ({children}) => <ol className="list-decimal ml-4 my-2 space-y-1">{children}</ol>,
                                li: ({children}) => <li className="leading-relaxed">{children}</li>,
                                strong: ({children}) => <strong className="font-semibold">{children}</strong>,
                              }}
                            >
                              {coveragePlan.coverage_plan}
                            </ReactMarkdown>
                          </div>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Recommendations Tab */}
        <TabsContent value="ai-recommendations" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="h-5 w-5 text-[#00b0ab]" />
                    AI-Powered Capacity Recommendations
                  </CardTitle>
                  <CardDescription>
                    Get intelligent recommendations based on your RN/CM availability data
                  </CardDescription>
                </div>
                <Button onClick={handleAIAnalysis} disabled={analyzingAI} data-testid="btn-ai-analysis">
                  {analyzingAI ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generate AI Analysis
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!aiAnalysis ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Brain className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">No AI Analysis Yet</p>
                  <p className="text-sm mt-2">
                    Click "Generate AI Analysis" to get intelligent capacity recommendations<br />
                    based on your current RN/CM availability data.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm text-muted-foreground border-b pb-2">
                    <span>Generated: {new Date(aiAnalysis.generated_at).toLocaleString()}</span>
                    <div className="flex gap-4">
                      <span>Clinicians: {aiAnalysis.projection_summary?.total_clinicians}</span>
                      <span>Utilization: {aiAnalysis.projection_summary?.utilization}%</span>
                    </div>
                  </div>
                  
                  <ScrollArea className="h-[600px] rounded-lg border p-4 bg-muted/30">
                    <div className="prose prose-sm max-w-none dark:prose-invert">
                      <ReactMarkdown
                        components={{
                          h1: ({children}) => <h1 className="text-xl font-bold mt-4 mb-2 text-[#00b0ab] dark:text-[#33c3bf]">{children}</h1>,
                          h2: ({children}) => <h2 className="text-lg font-bold mt-4 mb-2 text-[#00b0ab] dark:text-[#33c3bf]">{children}</h2>,
                          h3: ({children}) => <h3 className="text-base font-semibold mt-3 mb-1 text-[#00b0ab] dark:text-[#33c3bf]">{children}</h3>,
                          p: ({children}) => <p className="my-2 leading-relaxed">{children}</p>,
                          ul: ({children}) => <ul className="list-disc ml-4 my-2 space-y-1">{children}</ul>,
                          ol: ({children}) => <ol className="list-decimal ml-4 my-2 space-y-1">{children}</ol>,
                          li: ({children}) => <li className="leading-relaxed">{children}</li>,
                          strong: ({children}) => <strong className="font-semibold text-foreground">{children}</strong>,
                        }}
                      >
                        {aiAnalysis.analysis}
                      </ReactMarkdown>
                    </div>
                  </ScrollArea>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Clinician Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-[#00b0ab]" />
              Add New Clinician
            </DialogTitle>
            <DialogDescription>
              Add a new clinician to the capacity projection system
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={newClinician.clinician_name}
                  onChange={(e) => setNewClinician({...newClinician, clinician_name: e.target.value})}
                  placeholder="e.g., John Smith RN"
                />
              </div>
              <div>
                <Label htmlFor="hire_date">Hire Date</Label>
                <Input
                  id="hire_date"
                  value={newClinician.hire_date}
                  onChange={(e) => setNewClinician({...newClinician, hire_date: e.target.value})}
                  placeholder="e.g., 1/15/26"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="team">Team</Label>
                <Select value={newClinician.team} onValueChange={(v) => setNewClinician({...newClinician, team: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TEAMS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="discipline">Discipline</Label>
                <Select value={newClinician.discipline} onValueChange={(v) => setNewClinician({...newClinician, discipline: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DISCIPLINES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="visits">Weekly Visits</Label>
                <Input
                  id="visits"
                  type="number"
                  value={newClinician.total_visits}
                  onChange={(e) => setNewClinician({...newClinician, total_visits: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label htmlFor="exp_min">Min Expected</Label>
                <Input
                  id="exp_min"
                  type="number"
                  value={newClinician.expectation_min}
                  onChange={(e) => setNewClinician({...newClinician, expectation_min: parseInt(e.target.value) || 15})}
                />
              </div>
              <div>
                <Label htmlFor="exp_max">Max Expected</Label>
                <Input
                  id="exp_max"
                  type="number"
                  value={newClinician.expectation_max}
                  onChange={(e) => setNewClinician({...newClinician, expectation_max: parseInt(e.target.value) || 20})}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="comment">Notes/Comments</Label>
              <Input
                id="comment"
                value={newClinician.comment}
                onChange={(e) => setNewClinician({...newClinician, comment: e.target.value})}
                placeholder="e.g., PTO scheduled..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleAddClinician} disabled={savingClinician || !newClinician.clinician_name}>
              {savingClinician ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Add Clinician
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Clinician Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-[#00b0ab]" />
              Edit Clinician
            </DialogTitle>
            <DialogDescription>
              Update clinician information
            </DialogDescription>
          </DialogHeader>
          {selectedClinician && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  <Input
                    value={selectedClinician.clinician_name || ''}
                    onChange={(e) => setSelectedClinician({...selectedClinician, clinician_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Hire Date</Label>
                  <Input
                    value={selectedClinician.hire_date || ''}
                    onChange={(e) => setSelectedClinician({...selectedClinician, hire_date: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Team</Label>
                  <Select value={selectedClinician.team} onValueChange={(v) => setSelectedClinician({...selectedClinician, team: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TEAMS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Discipline</Label>
                  <Select value={selectedClinician.discipline || 'RN'} onValueChange={(v) => setSelectedClinician({...selectedClinician, discipline: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DISCIPLINES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Weekly Visits</Label>
                  <Input
                    type="number"
                    value={selectedClinician.total_visits || 0}
                    onChange={(e) => setSelectedClinician({...selectedClinician, total_visits: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Avg Time (min)</Label>
                  <Input
                    type="number"
                    value={selectedClinician.avg_time_minutes || 60}
                    onChange={(e) => setSelectedClinician({...selectedClinician, avg_time_minutes: parseInt(e.target.value) || 60})}
                  />
                </div>
                <div>
                  <Label>Expected (min-max)</Label>
                  <div className="flex gap-1">
                    <Input
                      type="number"
                      value={selectedClinician.expectation_min || 15}
                      onChange={(e) => setSelectedClinician({...selectedClinician, expectation_min: parseInt(e.target.value) || 15})}
                      className="w-16"
                    />
                    <span className="py-2">-</span>
                    <Input
                      type="number"
                      value={selectedClinician.expectation_max || 20}
                      onChange={(e) => setSelectedClinician({...selectedClinician, expectation_max: parseInt(e.target.value) || 20})}
                      className="w-16"
                    />
                  </div>
                </div>
              </div>
              <div>
                <Label>Notes/Comments</Label>
                <Input
                  value={selectedClinician.comment || ''}
                  onChange={(e) => setSelectedClinician({...selectedClinician, comment: e.target.value})}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateClinician} disabled={savingClinician}>
              {savingClinician ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Clinician?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedClinician?.clinician_name}</strong>? 
              This action cannot be undone. All weekly data for this clinician will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteClinician} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
